import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createGame, clearError } from '@/store/gameSlice';
import { actionMessage } from '@/game/actionError';
import { selectUser } from '@/store/authSlice';
import { ProfileMenu } from '@/components/ProfileMenu/ProfileMenu';
import type { AppDispatch } from '@/store';
import './GameSetup.css';

// Generate random player names
function generatePlayerNames(count: number = 6): string[] {
  const firstNames = [
    "Astro", "Nova", "Luna", "Stellar", "Cosmo", "Orbit",
    "Rocket", "Comet", "Meteor", "Galaxy", "Nebula", "Pulsar",
    "Quasar", "Aurora", "Solar", "Lunar", "Stellar", "Cosmic",
    "Zenith", "Eclipse", "Phoenix", "Orion", "Atlas", "Vega",
    "Sirius", "Rigel", "Deneb", "Altair", "Castor", "Pollux",
  ];
  
  const lastNames = [
    "Explorer", "Pioneer", "Voyager", "Navigator", "Ranger", "Scout",
    "Commander", "Captain", "Major", "Colonel", "Admiral", "General",
    "Pilot", "Scientist", "Engineer", "Technician", "Specialist", "Expert",
    "Adventurer", "Pathfinder", "Trailblazer", "Discoverer", "Seeker", "Hunter",
  ];
  
  const names: string[] = [];
  const used = new Set<string>();
  
  while (names.length < count) {
    const first = firstNames[Math.floor(Math.random() * firstNames.length)];
    const last = lastNames[Math.floor(Math.random() * lastNames.length)];
    const name = `${first} ${last}`;
    
    if (!used.has(name)) {
      used.add(name);
      names.push(name);
    }
  }
  
  return names;
}

export function GameSetupPage() {
  const { levelId } = useParams<{ levelId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const { isLoading, error } = useSelector((state: any) => state.game);

  const [maxPlayers, setMaxPlayers] = useState(4);
  const [fillWithAI, setFillWithAI] = useState(true);
  const [availableNames, setAvailableNames] = useState<string[]>([]);
  const [selectedName, setSelectedName] = useState<string>('');

  const playerOptions = [2, 3, 4, 5, 6];

  // Clear any previous errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Generate names on component mount
  useEffect(() => {
    const names = generatePlayerNames(6);
    setAvailableNames(names);
    setSelectedName(names[0]); // Default to first name
  }, []);

  const handleRegenerateNames = () => {
    const names = generatePlayerNames(6);
    setAvailableNames(names);
    setSelectedName(names[0]);
  };

  const handleCreateGame = async () => {
    if (!selectedName) {
      alert('Please select a player name');
      return;
    }
    try {
      const result = await dispatch(
        createGame({
          levelId: levelId || 'lunar',
          maxPlayers,
          isPublic: false,
          waitTimeMinutes: 5,
          fillWithAI,
          playerName: selectedName, // Pass selected name
          availableNames: availableNames.filter(n => n !== selectedName), // Pass unused names for AI
          username: user?.username, // Pass username from Redux
          email: user?.email, // Pass email from Redux
        })
      );

      if (createGame.fulfilled.match(result)) {
        // Navigate to avatar selection with game ID
        const gameId = result.payload.gameId;
        navigate(`/lobby/avatars/${gameId}`);
      } else if (createGame.rejected.match(result)) {
        alert(`Failed to create game: ${actionMessage(result, 'Failed to create game')}`);
      }
    } catch (err: any) {
      console.error('Error creating game:', err);
      alert(`Error: ${err.message || 'Failed to create game'}`);
    }
  };

  const handleBack = () => {
    navigate('/lobby');
  };

  const getLevelName = (id: string) => {
    const names: Record<string, string> = {
      lunar: 'Lunar Outpost',
      martian: 'Martian Colony',
      asteroid: 'Asteroid Mining',
      station: 'Space Station',
      dyson: 'Dyson Sphere',
      wormhole: 'Wormhole Gateway',
    };
    return names[id] || 'Unknown Level';
  };

  return (
    <div className="game-setup-page">
      <header className="setup-header">
        <h1>R.U.S.H.</h1>
        <div className="user-info">
          <span>Welcome, {user?.name || user?.username || user?.email}</span>
          <ProfileMenu />
        </div>
      </header>

      <main className="setup-main">
        <div className="setup-container">
          {isLoading ? (
            <div className="constructing-game">
              <div className="spinner"></div>
              <h2>Constructing Game...</h2>
              <p>Setting up your lunar outpost</p>
            </div>
          ) : (
            <>
              <button onClick={handleBack} className="back-button">
                ← Back to Levels
              </button>

              <h2 className="setup-title">{getLevelName(levelId || 'lunar')}</h2>
              <p className="setup-subtitle">Configure your game settings</p>

              <div className="game-config">
                {/* Player Name Selection */}
                <div className="config-group name-selection">
                  <label htmlFor="playerName">Choose Your Player Name</label>
                  <div className="name-selector">
                    <select
                      id="playerName"
                      value={selectedName}
                      onChange={(e) => setSelectedName(e.target.value)}
                      className="name-select"
                    >
                      {availableNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleRegenerateNames}
                      className="regenerate-button"
                      title="Generate new name options"
                    >
                      🎲
                    </button>
                  </div>
                  <p className="name-hint">Select a name from the list or roll for new options</p>
                </div>

                <div className="config-group">
                  <label htmlFor="maxPlayers">Number of Players</label>
                  <select
                    id="maxPlayers"
                    value={maxPlayers}
                    onChange={(e) => setMaxPlayers(Number(e.target.value))}
                    className="player-select"
                  >
                    {playerOptions.map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'Player' : 'Players'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="config-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={fillWithAI}
                      onChange={(e) => setFillWithAI(e.target.checked)}
                    />
                    <span>Fill remaining slots with AI players</span>
                  </label>
                </div>
              </div>

              {/* Placeholder Invite Button */}
              <button
                disabled
                className="invite-button"
                title="Coming soon: Invite other players to join your game"
              >
                Invite Players (Coming Soon)
              </button>

              <button
                onClick={handleCreateGame}
                disabled={isLoading}
                className="create-game-button"
              >
                Create Game
              </button>

              {error && (
                <div className="error-message">
                  Error: {error}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

